// Updated version of the LochGenerator.vue component
<template>
  <div class="loch-generator">
    <div class="shape-display">
      <div v-if="similarHoles.length > 0 && !isMorphingActive" class="similarity-info">
        
        <button @click="downloadHole" class="">
          <img src="/assets/svgs/download.svg" class=" download-icon" alt="Download" />
        </button>
      </div>
      <svg ref="lochSvg" viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid meet">
        <path :d="currentPath" fill="currentColor" ref="lochPath" />
      </svg>
      
      
    </div>
    <a @click="toggleMorphing" class="center" >
        {{ isMorphingActive ? 'Stop' : 'Start' }}
      </a>

      

    <!-- Desktop view - collapsible control panel -->
    <div class="sliders-panel-container">
      <!-- Collapsed state - just the icon -->
      <div v-if="isPanelCollapsed" class="panel-toggle-icon" @click="togglePanel">
        <img src="/assets/svgs/regler.svg" alt="Open controls" class="regler-icon" />
      </div>
      
      <!-- Expanded state - full control panel -->
      <div v-else class="sliders-panel">
        <div class="panel-header">
      
          <div class="close-button" @click="togglePanel">
            <img src="/assets/svgs/cross.svg" alt="Close" />
          </div>
        </div>
            <h2 class="center">Wie soll das Loch aussehen?</h2>
        <div class="panel-content">
          <div v-for="cat in displayedCategories" :key="cat.category_id" class="slider-groupG" :data-category-id="cat.category_id">
            <div class="slider-labelsG">
              <span class="start-word">{{ cat.start_word }}</span>
              <span class="end-word">{{ cat.end_word }}</span>
            </div>
            <input
              type="range"
              v-model="cat.value"
              min="0"
              max="100"
              @click="toggleCategory(cat.category_id)"
              @dblclick="resetCategory(cat.category_id)"
              @change="onSliderRelease(cat.category_id)"
              @input="updateSliderBackground($event)"
              class="slider nowidth"
              :class="{ 'active-slider': cat.active }"
              :data-category-id="cat.category_id"
            />
          </div>
        </div>
      </div>
    
    </div>
    
  </div>
  
</template>

<script>
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import * as flubber from 'flubber'
import sessionKonto from '../js/sessionKonto'
import svgPathsData from './preprocessed_paths.json'
import morphingService from '../services/morphingService'
// In LochGenerator.vue's <script> section
import { gsap } from 'gsap' // This should use the same GSAP instance where plugins are registered
import '../styles/formenBewerter.css';

export default {
  name: 'LochGenerator',
  
  setup() {
    const currentPath = ref('')
    const categories = ref([])
    const userLevel = ref(0)
    const similarHoles = ref([])
    const currentHoleIndex = ref(0)
    const userId = ref(null)
    const userRatings = ref(0)
    const progress = ref(0)
    const isLoading = ref(false)
    const isPanelCollapsed = ref(false) // Start with panel collapsed
    const isDesktop = ref(window.innerWidth >= 768)
    const isMorphingActive = ref(true)
    const lochPath = ref(null)
    const isStoppingRequested = ref(false) // New ref to track when stopping is requested

    // Watch window resize to toggle desktop/mobile view
    window.addEventListener('resize', () => {
      isDesktop.value = window.innerWidth >= 768
    })

    const konto = computed(() => userId.value ? userRatings.value : sessionKonto.value)

    const displayedCategories = computed(() => {
      return categories.value
        .slice(0, userLevel.value)
        .map(cat => ({
          ...cat,
          value: 50,
          active: false // All sliders start as inactive
        }))
    })

    const currentSimilarity = computed(() => {
      return similarHoles.value[currentHoleIndex.value]?.similarity || 0
    })

    // Modified toggleMorphing function to handle graceful stopping
    const toggleMorphing = () => {
      if (isMorphingActive.value) {
        // Request to stop morphing
        isStoppingRequested.value = true
        
        // Listen for the next animation frame completion
        morphingService.onNextComplete(() => {
          // This will be called when the current transition completes
          morphingService.pause() // Explicitly pause the timeline
          isMorphingActive.value = false
          isStoppingRequested.value = false
          
          // Create a temporary similar hole object for the current shape if needed
          const currentShapeId = getCurrentShapeId()
          if (currentShapeId && similarHoles.value.length === 0) {
            similarHoles.value = [{
              shape_id: currentShapeId,
              similarity: 100
            }]
          }
        })
      } else {
        // Start morphing immediately
        isMorphingActive.value = morphingService.play()
        // Clear similar holes when starting morphing
        if (isMorphingActive.value) {
          similarHoles.value = []
        }
      }
    }

    // Helper function to determine current shape ID
    const getCurrentShapeId = () => {
      const currentPath = lochPath.value?.getAttribute('d')
      if (!currentPath) return null
      
      // Find the shape ID that matches the current path
      return Object.keys(svgPathsData).find(id => 
        svgPathsData[id] === currentPath
      ) || Object.keys(svgPathsData)[0]
    }

    const initMorphing = async () => {
      // Initialize the morphing service with SVG paths data
      await morphingService.initWithPathData(svgPathsData)
      
      // Initialize with the path element
      nextTick(() => {
        if (lochPath.value) {
          morphingService.initMorphing(lochPath.value)
          // Start morphing by default
          morphingService.play()
        }
      })
    }

    const togglePanel = () => {
      isPanelCollapsed.value = !isPanelCollapsed.value
      
      // When reopening the panel, restore all slider backgrounds
      if (!isPanelCollapsed.value) {
        nextTick(() => {
          displayedCategories.value.forEach(cat => {
            const slider = document.querySelector(`input[type="range"][data-category-id="${cat.category_id}"]`)
            if (slider) {
              updateSliderBackgroundManually(slider, cat.value)
            }
          })
        })
      }
    }

    const resetCategory = (categoryId) => {
      // Immediately update UI first
      const slider = document.querySelector(`input[type="range"][data-category-id="${categoryId}"]`)
      if (slider) {
        slider.style.background = "transparent"
      }
      
      // Then update data model
      const category = displayedCategories.value.find(cat => cat.category_id === categoryId)
      if (category) {
        category.value = 50
        category.active = false
        
        // Defer API call to prioritize UI responsiveness
        setTimeout(() => {
          updateSimilarHoles()
        }, 0)
      }
    }

    const loadRandomShape = () => {
      const availableShapeIds = Object.keys(svgPathsData)
      if (!availableShapeIds.length) return
      
      const randomIndex = Math.floor(Math.random() * availableShapeIds.length)
      const randomId = parseInt(availableShapeIds[randomIndex])
      const newPath = svgPathsData[randomId]

      if (!currentPath.value) {
        currentPath.value = newPath
      } else {
        const interpolator = flubber.interpolate(currentPath.value, newPath, { maxSegmentLength: 2 })
        progress.value = 0
        gsap.to(progress, {
          value: 1,
          duration: 0.5,
          onUpdate: () => {
            currentPath.value = interpolator(progress.value)
          },
          ease: "power2.inOut"
        })
      }
    }

    const loadInitialShape = async () => {
      try {
        const token = document.cookie
          .split('; ')
          .find(row => row.startsWith('auth_token='))
          ?.split('=')[1]

        if (token) {
          const response = await fetch('https://laubloch.de/backend/account.php?action=get-stats', {
            headers: { 'Authorization': `Bearer ${token}` }
          })
          const data = await response.json()
          userLevel.value = data.level
          userId.value = data.user_id
          userRatings.value = data.ratings.user
        } else {
          userLevel.value = 1
        }

        await fetchTopCategories()
        loadRandomShape()
      } catch (error) {
        console.error('Failed to load user stats:', error)
        userLevel.value = 1
        await fetchTopCategories()
        loadRandomShape()
      }
    }

    const fetchTopCategories = async () => {
      try {
        const response = await fetch(`https://laubloch.de/backend/level_categories.php?level=${userLevel.value}`)
        const data = await response.json()
        if (data.status === 'success') {
          categories.value = data.data
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error)
      }
    }

    const onSliderRelease = async (categoryId) => {
      const category = displayedCategories.value.find(cat => cat.category_id === categoryId)
      if (!category) return
      
      // Category should already be active from the input event, but ensure it
      if (!category.active) {
        category.active = true;
      }
      
      // Prioritize UI updates by deferring API call
      setTimeout(() => {
        updateSimilarHoles()
      }, 0)
    }

    const updateSimilarHoles = async () => {
      // Get active categories (those marked as active)
      const activeCategories = displayedCategories.value
        .filter(cat => cat.active)
        .map(cat => ({
          category_id: cat.category_id,
          value: parseFloat(cat.value)
        }))

      if (activeCategories.length === 0) {
        similarHoles.value = []
        return
      }

      try {
        isLoading.value = true
        const response = await fetch('https://laubloch.de/backend/find_similar_holes.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            categories: activeCategories,
            minMatchRatio: 0.75 // Allow matching if at least 75% of categories match
          })
        })
        const data = await response.json()
        if (data.success && data.holes.length > 0) {
          similarHoles.value = data.holes
          currentHoleIndex.value = 0
          morphToShape(data.holes[0].shape_id)
          
          // Update morphing with similar holes
          if (morphingService.updateWithSimilarHoles(data.holes, svgPathsData)) {
            // Reinitialize morphing with the new paths
            nextTick(() => {
              if (lochPath.value) {
                morphingService.initMorphing(lochPath.value)
                if (isMorphingActive.value) {
                  morphingService.play()
                }
              }
            })
          }
        } else {
          console.log('No similar holes found or empty response')
          similarHoles.value = []
        }
      } catch (error) {
        console.error('Failed to fetch similar holes:', error)
        similarHoles.value = []
      } finally {
        isLoading.value = false
      }
    }

    const morphToShape = (shapeId) => {
      const newPath = svgPathsData[shapeId]
      if (!newPath) {
        console.error(`Shape ID ${shapeId} not found in svgPathsData`)
        return
      }
      
      if (!currentPath.value) {
        currentPath.value = newPath
        return
      }

      const interpolator = flubber.interpolate(currentPath.value, newPath, { maxSegmentLength: 2 })
      progress.value = 0
      gsap.to(progress, {
        value: 1,
        duration: 0.5,
        onUpdate: () => {
          currentPath.value = interpolator(progress.value)
        },
        ease: "power2.inOut"
      })
    }

    const toggleCategory = (categoryId) => {
      // Find the category and toggle active state if needed
      const category = displayedCategories.value.find(cat => cat.category_id === categoryId)
      if (!category) return
      
      // If it's not already active, set it active
      if (!category.active) {
        category.active = true
        // Defer API call to prioritize UI responsiveness
        setTimeout(() => {
          updateSimilarHoles()
        }, 0)
      }
    }

    // Helper function to update slider background without event
    const updateSliderBackgroundManually = (slider, value) => {
      const center = 50
      
      if (value === center) {
        slider.style.background = "transparent"
      } else if (value > center) {
        slider.style.background = `
          linear-gradient(
            to right,
            transparent ${center}%,
            black ${center}%,
            black ${value}%,
            transparent ${value}%
          )
        `
      } else {
        slider.style.background = `
          linear-gradient(
            to right,
            transparent ${value}%,
            black ${value}%,
            black ${center}%,
            transparent ${center}%
          )
        `
      }
    }
    
    const updateSliderBackground = (event) => {
      const slider = event.target
      const value = parseInt(slider.value)
      
      // Add data attribute to help with reset
      slider.setAttribute('data-category-id', slider.closest('.slider-groupG').dataset.categoryId)
      
      // Immediately make slider active (black) regardless of value
      slider.classList.add('active-slider')
      
      // Update slider appearance immediately
      updateSliderBackgroundManually(slider, value)
      
      // Also mark the category as active immediately
      const categoryId = parseInt(slider.dataset.categoryId)
      const category = displayedCategories.value.find(cat => cat.category_id === categoryId)
      if (category && !category.active) {
        category.active = true
      }
    }

    const downloadHole = async () => {
  if (similarHoles.value.length === 0) {
    console.log('No holes available to download')
    return
  }

  try {
    const currentHole = similarHoles.value[currentHoleIndex.value]
    const svgPath = svgPathsData[currentHole.shape_id]
    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000">
      <path d="${svgPath}" fill="black"/>
    </svg>`
    
    // Save the download in the database with the SVG path
    await fetch('https://laubloch.de/backend/save_download.php', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': document.cookie.split('; ')
          .find(row => row.startsWith('auth_token='))
          ?.split('=')[1] ? `Bearer ${document.cookie.split('; ')
          .find(row => row.startsWith('auth_token='))
          ?.split('=')[1]}` : ''
      },
      body: JSON.stringify({
        shape_id: currentHole.shape_id,
        user_id: userId.value,
        svgPath: svgPath
      })
    })
    
    // Create download link
    const blob = new Blob([svgContent], { type: 'image/svg+xml' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `hole_${currentHole.shape_id}.svg`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    // Deduct credits after successful download
    if (userId.value) {
      userRatings.value = Math.max(0, userRatings.value - 1)
      await fetch('https://laubloch.de/backend/updateKonto.php', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${document.cookie
            .split('; ')
            .find(row => row.startsWith('auth_token='))
            ?.split('=')[1]}`
        },
        body: JSON.stringify({
          user_id: userId.value,
          konto: userRatings.value
        })
      })
    } else {
      sessionKonto.value = Math.max(0, sessionKonto.value - 1)
    }
  } catch (error) {
    console.error('Failed to download hole:', error)
  }
}

    onMounted(() => {
      loadInitialShape()
      initMorphing()
    })

    // Add a watch for category value changes to update similar holes
    watch(displayedCategories, () => {
      // Update similar holes when any active category value changes
      const hasActiveCategories = displayedCategories.value.some(cat => cat.active)
      if (hasActiveCategories) {
        updateSimilarHoles()
      }
    }, { deep: true })

    return {
      currentPath,
      displayedCategories,
      similarHoles,
      currentSimilarity,
      konto,
      isLoading,
      isDesktop,
      isPanelCollapsed,
      isMorphingActive,
      lochPath,
      toggleCategory,
      resetCategory,
      onSliderRelease,
      updateSliderBackground,
      updateSliderBackgroundManually,
      downloadHole,
      togglePanel,
      toggleMorphing
    }
  }
}
</script>
